import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Checkbox } from "@/components/ui/checkbox";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { LockKey, ShieldCheck, ArrowLeft, ArrowUpRight, MapPin } from "@phosphor-icons/react";
import { SiteBrand } from "@/components/site/SiteBrand";
import "@/styles/diotech-site.css";

export default function LoginPage() {
  useDocumentTitle("Accesso");
  const { login, error } = useAuth();
  const { pushOverride, popOverride } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [remember, setRemember] = useState(true);
  const [step, setStep] = useState("credentials");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    pushOverride("light");
    return () => popOverride();
  }, [pushOverride, popOverride]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res === "2fa") setStep("totp");
  };

  const onSubmitTotp = async (e) => {
    e.preventDefault();
    setLoading(true);
    await login(email, password, otp, { remember_device: remember });
    setLoading(false);
  };

  return (
    <div className="dt-site dt-hero-bg relative overflow-hidden flex flex-col" data-testid="login-page">
      <div className="dt-grain" aria-hidden="true" />
      <div className="relative z-10 dt-container flex items-center justify-between h-[91px]">
        <SiteBrand />
        <a href="https://www.diotech.it" className="dt-nav-link text-xs inline-flex items-center gap-2" data-testid="login-back-to-site">
          www.diotech.it <ArrowUpRight size={14} />
        </a>
      </div>

      <main className="relative z-10 flex-1 dt-container grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-20 items-center py-10 lg:py-16">
        <div className="dt-rise">
          <div className="dt-eyebrow mb-6"><span className="dt-square" /> Diotech / Area riservata</div>
          <h1 className="dt-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.06em]">
            Il gestionale.<br />
            <span className="text-[#ff852a]">Ogni intervento,<br />sotto controllo.</span>
          </h1>
          <p className="mt-6 max-w-md text-[15px] leading-[1.75] text-[#b9bcbd]">
            Interventi, clienti, piani di assistenza e moduli in un unico spazio di lavoro riservato agli operatori DIOTECH.
          </p>
          <div className="mt-8 flex items-center gap-2 text-[11px] text-[#8d9092]">
            <ShieldCheck size={16} className="text-[#ff852a]" /> Accesso protetto con verifica in due passaggi.
          </div>
        </div>

        <div className="dt-paper p-7 sm:p-9 w-full max-w-[480px] lg:justify-self-end dt-rise dt-d2">
          {step === "credentials" && (
            <form onSubmit={onSubmit} data-testid="login-form">
              <span className="dt-kicker">Accesso operatore</span>
              <h2 className="dt-display text-3xl mt-2 mb-7">Bentornato.</h2>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="dt-label">Email <span className="dt-req">*</span></label>
                  <input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    required placeholder="nome@diotech.it" className="dt-field" data-testid="login-email-input" />
                </div>
                <div>
                  <label htmlFor="password" className="dt-label">Password <span className="dt-req">*</span></label>
                  <input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
                    required placeholder="••••••••" className="dt-field" data-testid="login-password-input" />
                </div>
              </div>
              {error && <div data-testid="login-error" className="mt-4 text-sm text-[#b3261e] border-l-2 border-[#b3261e] bg-[#fbe9e7] px-3 py-2">{error}</div>}
              <button type="submit" disabled={loading} data-testid="login-submit-btn" className="dt-btn dt-btn-ink w-full justify-between mt-6">
                {loading ? "Accesso in corso…" : "Accedi al gestionale"} <LockKey size={18} weight="bold" />
              </button>
              <p className="mt-4 text-[11px] text-[#969e8c]">Accedi con le tue credenziali operatore.</p>
            </form>
          )}

          {step === "totp" && (
            <form onSubmit={onSubmitTotp} data-testid="login-totp-form">
              <span className="dt-kicker">Verifica in due passaggi</span>
              <h2 className="dt-display text-3xl mt-2 mb-3">Codice 2FA.</h2>
              <p className="text-sm text-[#5b6152] mb-6">Apri l'app di autenticazione e inserisci il codice a 6 cifre.</p>
              <div className="flex justify-center mb-6">
                <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="login-totp-input" autoFocus>
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="bg-[#f2f3eb] border-[#d7dbce] h-12 w-11 text-lg" />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <label className="flex items-start gap-2.5 select-none cursor-pointer">
                <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="login-remember-device" className="mt-0.5" />
                <div className="text-sm leading-snug">
                  <div className="font-medium">Fidati di questo dispositivo per 90 giorni</div>
                  <div className="text-xs text-[#7a8070]">Non ti verrà più chiesto il codice 2FA su questo PC/browser fino alla scadenza. Non selezionare su computer condivisi.</div>
                </div>
              </label>
              {error && <div data-testid="login-error" className="mt-4 text-sm text-[#b3261e] border-l-2 border-[#b3261e] bg-[#fbe9e7] px-3 py-2">{error}</div>}
              <div className="flex gap-2 mt-6">
                <button type="button" onClick={() => { setStep("credentials"); setOtp(""); }} className="dt-btn dt-btn-ghost" data-testid="login-back-btn">
                  <ArrowLeft size={16} /> Indietro
                </button>
                <button type="submit" disabled={loading || otp.length !== 6} data-testid="login-totp-submit-btn" className="dt-btn dt-btn-ink flex-1 justify-between">
                  {loading ? "Verifico…" : "Verifica e accedi"} <ShieldCheck size={18} weight="bold" />
                </button>
              </div>
            </form>
          )}
        </div>
      </main>

      <div className="relative z-10 dt-container border-t border-white/10 py-5 flex flex-col sm:flex-row justify-between gap-2 text-[11px] text-[#8a9393]">
        <span className="inline-flex items-center gap-2"><MapPin size={14} className="text-[#ff852a]" /> Vigevano · Assistenza da remoto e in sede</span>
        <span>© {new Date().getFullYear()} DIOTECH S.r.l.s.</span>
      </div>
    </div>
  );
}
